// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { Icon } from '@lmc/ui-kit'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import styled from 'styled-components'
import { AntdSteps } from '../AntdSteps'
import Dot from '../Dot/Dot'
import { IconSectionHeader } from '../IconSectionHeader'

const isDesktop = isBrowser || isTablet

export default function StepList(props) {
  const {
    backIcon,
    backIconColor,
    backText,
    children,
    closeLabel,
    disableNavigation = false,
    displayDots,
    displaySelection,
    displaySubmit,
    header,
    headerProps,
    modalTitle,
    onClose,
    stepLabels,
    stepStyle,
    stepsSubheader,
    useAntdSteps
  } = props

  const [currentStep, setCurrentStep] = useState(props.initialStep || 0)
  const [displayStep, setDisplayStep] = useState(props.initialStep || 0)
  const [totalSteps, setTotalStepsState] = useState(0)

  useEffect(() => {
    if (children && children.length) {
      setTotalStepsState(_.compact(children).length - 1)
    }
  }, [children])

  const setPreviousStep = () => {
    setCurrentStep((prev) => {
      let target = prev - 1
      while (target > 0 && !children[target]) {
        target--
      }
      return target
    })
  }

  const setNextStep = () => {
    setCurrentStep((prev) => {
      let target = prev + 1
      while (target < children.length - 1 && !children[target]) {
        target++
      }
      return target
    })
  }

  const goToPreviousStep = () => {
    setDisplayStep((prev) => prev - 1)
    setPreviousStep()
  }

  const goToNextStep = () => {
    setDisplayStep((prev) => prev + 1)
    setNextStep()
  }

  const getDots = () => {
    return useAntdSteps ? (
      <div>
        {modalTitle}
        <AntdSteps
          className="lmc-add-med-antd-steps"
          currentStep={displayStep}
          stepLabels={stepLabels}
          finalStepIndex={totalSteps}
        />
      </div>
    ) : (
      _.range(0, totalSteps + 1).map((d, i) => (
        <DotDiv key={i} $active={d === displayStep} />
      ))
    )
  }

  const renderBackIcon = () => {
    if (displaySelection === false) return null

    if ((!backIcon && !backIconColor) || currentStep === 0) {
      return headerProps ? <SelectionContainer /> : null
    }
    const iconColor = backIconColor || colors.background
    return (
      <SelectionContainer
        style={{
          paddingTop: headerProps ? (isDesktop ? 130 : 45) : 80
        }}
      >
        <SelectionLink>
          <IconButtonDiv style={{ backgroundColor: iconColor }}>
            <IconImg alt="" src={backIcon} />
          </IconButtonDiv>
          {backText}
        </SelectionLink>
      </SelectionContainer>
    )
  }

  const renderBackGlyph = () => {
    if (currentStep === 0)
      return (
        <BackGlyph>
          <NoGlyph />
        </BackGlyph>
      )

    return (
      <BackGlyph>
        <SelectionLink as="a" onClick={goToPreviousStep}>
          <Icon name="LeftOutlined" />
        </SelectionLink>
      </BackGlyph>
    )
  }

  const renderHeader = () => {
    const dots = displayDots === false ? null : getDots()

    if (isDesktop)
      return (
        <HeaderContainer>
          <CancelButton
            data-testid="close-modal"
            className="lmc-hoverable-opacity"
            onClick={onClose}
          >
            <Dot
              label={<Icon name="CloseOutlined" style={{ margin: 1 }} />}
              selectable
              color={colors.red}
              active
              size={24}
              fontSize={12}
            />
            <CancelText>{closeLabel}</CancelText>
          </CancelButton>
          <DotsContainer>{dots}</DotsContainer>
          {stepsSubheader}
          {headerProps ? (
            <SectionHeader>
              <IconSectionHeader {...headerProps} />
            </SectionHeader>
          ) : null}
        </HeaderContainer>
      )

    return (
      <div>
        <MobileHeaderContainer>
          {renderBackGlyph()}
          {displayDots && <DotsContainer>{dots}</DotsContainer>}
          <CancelButton className="lmc-hoverable-opacity" onClick={onClose}>
            <Dot
              label={<Icon name="CloseOutlined" />}
              selectable
              color={colors.red}
              active
              size={24}
              fontSize={12}
            />
          </CancelButton>
        </MobileHeaderContainer>
        {headerProps ? (
          <SectionHeader>
            <IconSectionHeader {...headerProps} />
          </SectionHeader>
        ) : null}
      </div>
    )
  }

  const steps = React.Children.map(children, (child, index) => {
    if (child)
      return React.cloneElement(child, {
        isActive: index === currentStep,
        displayPrevious: currentStep > 0 && !disableNavigation,
        displayNext: currentStep < children.length - 1 && !disableNavigation,
        displaySubmit:
          displaySubmit === false ? false : currentStep === children.length - 1,
        goToPreviousStep,
        goToNextStep,
        style: stepStyle
      })
  })

  return (
    <Container>
      {renderHeader()}
      {renderBackIcon()}
      {isDesktop && !disableNavigation && renderBackGlyph()}
      {header || null}
      {steps || null}
    </Container>
  )
}

const BackGlyph = styled.div`
  ${
    isDesktop &&
    `
    left: 20px;
    position: fixed;
    top: 50%;
    transform: translate(0%, -50%);
  `
  }
`

const CancelButton = styled.div`
  cursor: pointer;
  display: inline-block;
  ${
    isDesktop &&
    `
    margin-left: 50px;
    position: relative;
    top: 20px;
  `
  }
`

const CancelText = styled.span``

const Container = styled.div`
  background: ${colors.background};
  height: 100vh;
  left: 0;
  overflow: auto;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1000;
`

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
`

const DotDiv = styled.div<{ $active?: boolean }>`
  background-color: ${({ $active }) => ($active ? colors.pink : colors.grey)};
  border-radius: 50%;
  display: inline-block;
  height: 11px;
  margin: 0 8px;
  width: 11px;
`

const HeaderContainer = styled.div`
  background: ${colors.background};
  left: 0;
  padding-bottom: 20px;
  padding-top: 10px;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 10;
`

const IconButtonDiv = styled.div`
  border-radius: 10px;
  height: 30px;
  margin: 0 auto 5px;
  width: 30px;
`

const IconImg = styled.img`
  margin-top: 6px;
  width: 12px;
`

const MobileHeaderContainer = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: space-between;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
`

const NoGlyph = styled.div`
  background: ${colors.background};
  height: 64px;
  width: 33px;
`

const SectionHeader = styled.div`
  margin: auto;
  padding: ${isDesktop ? '20px 20px 0' : '70px 20px 0'};
  width: ${isDesktop ? '80%' : '100%'};
`

const SelectionContainer = styled.div`
  height: 70px;
  padding-top: 80px;
  text-align: center;
`

const SelectionLink = styled.a`
  color: ${colors.darkgrey};
  font-size: 15px;
  font-weight: 300;
  letter-spacing: 0.8px;
  padding-top: 8px;
`
